/**
 * Posts: the draft a person prepares — video, animated banner, the caption, how the phones are
 * uniquified (the strip's side, height, opacity, the sound), the title card, the phones — and, once
 * launched, one stage per phone.
 *
 * The draft is saved as it is edited (debounced `PUT /v1/posts/{id}`), so it can be closed and fixed
 * later. The preview is two things: the **exact frame** the service renders (`GET /v1/posts/{id}/frame`,
 * the same ffmpeg filters the variant goes through, strip included with the example sources), and the
 * picture **in motion** — the video, the strip (background + example overlay clip at the chosen opacity)
 * and the banner clip laid over each other in the browser with the same box arithmetic (`bannerBox.ts`).
 * The first is the truth, the second shows the animation. "Check" lists every problem at once; "Launch"
 * is hold-to-confirm and happens once: it makes real TikTok posts.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { CardPlacement, Device, Hub, OverlayChoice, Post, PostCheck, PostPhone, Source, SourceKind, StripSettings, StripSide, Video } from '../../api/types'
import { uploadSource } from '../../api/upload'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { toast } from '../../lib/toast'
import { Badge, Card, Empty, Status } from '../../ui/components'
import { ConfirmDanger } from '../../ui/ConfirmDanger'
import { TargetPicker, countPhones, fromTargets, toTargets, type Picked } from '../distribute/TargetPicker'
import { phoneName } from '../devices/phoneName'
import { ACCEPT } from '../sources/SourcesPanel'
import { videoName } from '../videos/VideosPanel'
import { BANNER_HEIGHT, BANNER_MARGIN, BANNER_WIDTH, bannerBoxStyle, clampBanner, type BannerGeometry } from './bannerBox'

const DEFAULT_BOX: BannerGeometry = { width: 1, height: 0.2, anchor: 'bottom', margin: 0 }
/** The engine's defaults (`uniquify.StripSettings`) — what a post without settings renders with. */
const DEFAULT_STRIP: StripSettings = {
  side: 'random',
  height_min: 0.25,
  height_max: 0.3,
  opacity_min: 0.55,
  opacity_max: 0.75,
  pitch_min: 0.01,
  pitch_max: 0.02,
  music_volume_min: 0.02,
  music_volume_max: 0.05,
  example_overlay_id: null,
  example_background_id: null,
}
const STRIP_HEIGHT: [number, number] = [0.01, 0.95]
const PITCH: [number, number] = [0, 0.5]

/** The side the preview shows: the chosen one, or the top when the post leaves it random per phone. */
function previewSide(side: StripSide): 'top' | 'bottom' {
  return side === 'random' ? 'top' : side
}

/** The source the preview puts in the strip: the chosen one when it is ready, else the oldest ready one. */
function exampleOf(sources: Source[], kind: SourceKind, chosen: string | null | undefined): Source | null {
  const ready = sources.filter((s) => s.kind === kind && s.status === 'ready')
  return ready.find((s) => s.source_id === chosen) ?? ready.slice().sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))[0] ?? null
}

function stageKind(stage: PostPhone['stage']): 'ok' | 'bad' | 'warn' | undefined {
  if (stage === 'done') return 'ok'
  if (stage === 'error' || stage === 'cancelled' || stage === 'not_posted') return 'bad'
  return 'warn'
}

export function PostsPanel() {
  const { id } = useParams()
  return id ? <PostPage postId={id} /> : <PostList />
}

function PostList() {
  const { api, auth, authEpoch } = useApp()
  const t = useT()
  const navigate = useNavigate()
  const posts = usePoll(() => api.posts(), 5000, [api, authEpoch])
  const videos = usePoll(() => api.videos(), 10000, [api, authEpoch])
  const [pick, setPick] = useState('')
  const [toDelete, setToDelete] = useState<Post | null>(null)
  const byId = new Map((videos.data ?? []).map((v) => [v.video_id, v]))

  const create = async (videoId: string) => {
    if (!videoId) return
    try {
      const post = await api.createPost({ video_id: videoId, text: '', targets: [] })
      navigate(`/posts/${post.post_id}`)
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    }
  }

  const remove = async () => {
    if (!toDelete) return
    try {
      await api.deletePost(toDelete.post_id)
      toast(t.posts.deleted)
      setToDelete(null)
      posts.refresh()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    }
  }

  const ready = (videos.data ?? []).filter((v) => v.status === 'ready')
  return (
    <>
      <Card
        title={t.posts.title}
        sub={t.posts.sub}
        actions={
          <select
            className="new-post"
            value={pick}
            onChange={(e) => {
              setPick('')
              void create(e.target.value)
            }}
          >
            <option value="">＋ {t.posts.newPick}</option>
            {ready.map((v) => (
              <option key={v.video_id} value={v.video_id}>
                {videoName(v)}
              </option>
            ))}
          </select>
        }
      >
        <Status text={posts.error ? posts.error.message : null} bad />
        {posts.data && posts.data.length === 0 && <Empty>{t.posts.none}</Empty>}
        {posts.data && posts.data.length > 0 && (
          <div className="scroll-x">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t.posts.video}</th>
                  <th>{t.posts.text}</th>
                  <th>{t.posts.phones}</th>
                  <th>{t.videos.state}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {posts.data.map((p) => {
                  const phones = p.phones ?? []
                  const done = phones.filter((x) => x.stage === 'done').length
                  const failed = phones.filter((x) => x.stage === 'error').length
                  return (
                    <tr key={p.post_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/posts/${p.post_id}`)}>
                      <td>{byId.get(p.video_id) ? videoName(byId.get(p.video_id) as Video) : p.video_id}</td>
                      <td className="dim small" style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.text || t.common.dash}
                      </td>
                      <td>
                        {p.phones ? (
                          <>
                            {done}/{phones.length}
                            {failed > 0 && <span style={{ color: 'var(--bad)' }}> · {t.posts.failedCount(failed)}</span>}
                          </>
                        ) : (
                          t.distribute.someOf(0, p.targets.length)
                        )}
                      </td>
                      <td className="col-state">
                        <Badge kind={p.status === 'launched' ? 'ok' : 'warn'}>{t.posts.status[p.status]}</Badge>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="danger btn-sm"
                          disabled={!auth.mayTouch(p.created_by)}
                          title={auth.mayTouch(p.created_by) ? t.posts.delete : t.common.notYours}
                          onClick={(e) => {
                            e.stopPropagation()
                            setToDelete(p)
                          }}
                        >
                          {t.posts.delete}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {toDelete && (
        <ConfirmDanger title={t.posts.deleteTitle} confirmLabel={t.posts.delete} holdSeconds={1} onCancel={() => setToDelete(null)} onConfirm={() => void remove()}>
          <p className="dim small">{t.posts.deleteBody}</p>
        </ConfirmDanger>
      )}
    </>
  )
}

function PostPage({ postId }: { postId: string }) {
  const { api, auth, authEpoch } = useApp()
  const t = useT()
  const navigate = useNavigate()
  const post = usePoll(() => api.post(postId), 4000, [api, authEpoch, postId])
  if (post.error) return <Status text={post.error.message} bad />
  if (!post.data) return <Empty>{t.distribute.loading}</Empty>
  return (
    <>
      <div className="row">
        <button className="ghost btn-sm" onClick={() => navigate('/posts')}>
          ← {t.posts.title}
        </button>
      </div>
      {post.data.status === 'launched' ? (
        <LaunchedPost post={post.data} onChanged={post.refresh} />
      ) : auth.mayTouch(post.data.created_by) ? (
        <PostEditor post={post.data} onLaunched={post.refresh} />
      ) : (
        //: Somebody else's draft: shown as it is, every control off (a disabled fieldset disables all of
        //: them, so the autosave never fires), the reason above — disabled, not hidden.
        <>
          <Status text={t.posts.notYoursEditor} bad />
          <fieldset className="not-yours" disabled title={t.common.notYours}>
            <PostEditor post={post.data} onLaunched={post.refresh} />
          </fieldset>
        </>
      )}
    </>
  )
}

const DEFAULT_OVERLAY: OverlayChoice = { text: '', nx: 0.5, ny: 0.5, size: 0.05, width: 0.9, color: '#FFFFFF', background: '#00000099' }

/** `#RRGGBB` + opacity 0..1 → `#RRGGBBAA`; the backing colour input holds the RGB, the slider the alpha. */
function withAlpha(rgb: string, opacity: number): string {
  const a = Math.round(Math.min(1, Math.max(0, opacity)) * 255)
  return `${rgb.slice(0, 7)}${a.toString(16).padStart(2, '0').toUpperCase()}`
}

function splitAlpha(color: string | null | undefined): { rgb: string; opacity: number } {
  if (!color) return { rgb: '#000000', opacity: 0.6 }
  const rgb = color.slice(0, 7)
  const a = color.length === 9 ? parseInt(color.slice(7, 9), 16) / 255 : 1
  return { rgb, opacity: Number.isFinite(a) ? a : 1 }
}

interface Step { label: string; done: boolean; optional?: boolean; need?: string | null }

/** The steps of a post, numbered, each saying whether it is done or what it still needs; a click goes to
 *  the step. Optional steps say so — a post needs only the video, its caption, the phones and the check. */
function StepBar({ steps }: { steps: Step[] }) {
  const t = useT()
  const go = (n: number) => document.getElementById(`post-step-${n}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  return (
    <ol className="step-bar" aria-label={t.posts.wizard.title}>
      {steps.map((s, i) => (
        <li key={i} className={s.done && !s.optional ? 'done' : s.optional ? 'optional' : 'todo'}>
          <button type="button" onClick={() => go(i + 1)}>
            <span className="step-n">{s.done && !s.optional ? '✓' : i + 1}</span>
            <span className="step-text">
              <span className="step-label">{s.label}</span>
              <span className="tiny">{s.optional ? t.posts.wizard.optional : s.done ? t.posts.wizard.done : s.need}</span>
            </span>
          </button>
        </li>
      ))}
    </ol>
  )
}

function StepTitle({ n, label, done, optional = false }: { n: number; label: string; done: boolean; optional?: boolean }) {
  const t = useT()
  return (
    <span className="step-title" id={`post-step-${n}`}>
      <span className={`step-n${done && !optional ? ' done' : ''}`}>{done && !optional ? '✓' : n}</span>
      {label}
      {optional && <span className="tiny dim">{t.posts.wizard.optional}</span>}
    </span>
  )
}

/** The draft: every change is saved after a short pause, the preview follows the banner box live. */
function PostEditor({ post, onLaunched }: { post: Post; onLaunched: () => void }) {
  const { api, authEpoch } = useApp()
  const t = useT()
  const videos = usePoll(() => api.videos(), 10000, [api, authEpoch])
  const sources = usePoll(() => api.sources(), 10000, [api, authEpoch])
  const [hubs, setHubs] = useState<Hub[] | null>(null)
  const [devices, setDevices] = useState<Device[]>([])
  const [videoId, setVideoId] = useState(post.video_id)
  const [text, setText] = useState(post.text)
  const [bannerId, setBannerId] = useState(post.banner?.source_id ?? '')
  const [box, setBox] = useState<BannerGeometry>(post.banner ? { width: post.banner.width, height: post.banner.height, anchor: post.banner.anchor, margin: post.banner.margin } : DEFAULT_BOX)
  const [strip, setStrip] = useState<StripSettings>({ ...DEFAULT_STRIP, ...(post.strip ?? {}) })
  const [cardId, setCardId] = useState(post.card?.source_id ?? '')
  const [placement, setPlacement] = useState<CardPlacement>(post.card?.placement ?? 'end')
  const [cardSeconds, setCardSeconds] = useState(post.card?.seconds ?? 2)
  const [overlayOn, setOverlayOn] = useState(Boolean(post.overlay))
  const [overlay, setOverlay] = useState<OverlayChoice>(post.overlay ?? DEFAULT_OVERLAY)
  const [backing, setBacking] = useState<boolean>(Boolean(post.overlay ? post.overlay.background : DEFAULT_OVERLAY.background))
  const [picked, setPicked] = useState<Picked>(fromTargets(post.targets))
  const [saved, setSaved] = useState<'saved' | 'saving' | 'dirty'>('saved')
  const [version, setVersion] = useState(0)
  const [check, setCheck] = useState<PostCheck | null>(null)
  const [confirm, setConfirm] = useState(false)
  const [holdLaunch, setHoldLaunch] = useState(false)
  const [bannerBusy, setBannerBusy] = useState(false)
  const bannerInput = useRef<HTMLInputElement>(null)
  const firstRender = useRef(true)

  useEffect(() => {
    void Promise.all([api.hubs(), api.devices()]).then(([h, d]) => {
      setHubs(h)
      setDevices(d)
    })
  }, [api])

  const overlayOut: OverlayChoice | null = overlayOn && overlay.text.trim() ? { ...overlay, background: backing ? overlay.background : null } : null

  // Save after the person stops for a moment. The first render is the post as loaded — nothing to save.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    setSaved('dirty')
    setCheck(null)
    const handle = setTimeout(() => {
      setSaved('saving')
      api
        .editPost(post.post_id, {
          video_id: videoId,
          text,
          banner: bannerId ? { source_id: bannerId, ...box } : null,
          overlay: overlayOut,
          card: cardId ? { source_id: cardId, placement, seconds: cardSeconds } : null,
          strip,
          targets: toTargets(picked),
        })
        .then(() => {
          setSaved('saved')
          setVersion((v) => v + 1)
        })
        .catch((e) => {
          setSaved('dirty')
          toast(e instanceof Error ? e.message : String(e), 'bad')
        })
    }, 600)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, post.post_id, videoId, text, bannerId, box, picked, overlayOn, overlay, backing, strip, cardId, placement, cardSeconds])

  const readyVideos = (videos.data ?? []).filter((v) => v.status === 'ready')
  const video = (videos.data ?? []).find((v) => v.video_id === videoId) ?? null
  const banners = (sources.data?.sources ?? []).filter((s) => s.kind === 'banner' && s.status === 'ready')
  const banner = banners.find((b) => b.source_id === bannerId) ?? null
  const allSources = sources.data?.sources ?? []
  const overlays = allSources.filter((s) => s.kind === 'overlay' && s.status === 'ready')
  const backgrounds = allSources.filter((s) => s.kind === 'background' && s.status === 'ready')
  const cards = allSources.filter((s) => s.kind === 'card' && s.status === 'ready')
  const exampleOverlay = exampleOf(allSources, 'overlay', strip.example_overlay_id)
  const exampleBackground = exampleOf(allSources, 'background', strip.example_background_id)
  const phoneCount = countPhones(picked, devices, video?.direction_id)
  const capacity = sources.data?.capacity ?? 0

  const uploadBanner = async (file: File | undefined) => {
    if (!file) return
    setBannerBusy(true)
    try {
      const uploaded = await uploadSource(api, 'banner', file, () => {})
      if (uploaded.status !== 'ready') throw new Error(uploaded.error ?? t.videos.rejected)
      sources.refresh()
      setBannerId(uploaded.source_id)
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    } finally {
      setBannerBusy(false)
    }
  }

  const runCheck = async () => {
    try {
      setCheck(await api.checkPost(post.post_id))
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    }
  }

  const launch = async () => {
    setConfirm(false)
    try {
      const started = await api.launchPost(post.post_id, holdLaunch)
      toast(t.posts.launched(started.hubs.length))
      onLaunched()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
      void runCheck()
    }
  }

  const percent = (v: number) => `${Math.round(v * 100)}%`
  const slider = (label: string, value: number, [lo, hi]: [number, number], onChange: (v: number) => void, step = 0.01, fmt = percent) => (
    <div className="slider-row" key={label}>
      <span className="small">{label}</span>
      <input type="range" min={lo} max={hi} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="mono small">{fmt(value)}</span>
    </div>
  )
  /** A min/max pair that cannot cross: each phone's value is drawn between the two. */
  const range = (label: string, lo: number, hi: number, bounds: [number, number], onChange: (lo: number, hi: number) => void, step = 0.01, fmt = percent) => (
    <>
      {slider(`${label} ${t.posts.strip.min}`, lo, bounds, (v) => onChange(v, Math.max(v, hi)), step, fmt)}
      {slider(`${label} ${t.posts.strip.max}`, hi, bounds, (v) => onChange(Math.min(lo, v), v), step, fmt)}
    </>
  )
  const chips = <K extends string>(options: K[], value: K, labels: Record<string, string>, onChange: (v: K) => void) => (
    <span className="chips">
      {options.map((o) => (
        <button key={o} className={`chip${value === o ? ' on' : ''}`} onClick={() => onChange(o)}>
          {labels[o] ?? o}
        </button>
      ))}
    </span>
  )
  const sourceSelect = (label: string, value: string | null | undefined, options: Source[], fallback: Source | null, onChange: (id: string | null) => void) => (
    <div className="field" key={label}>
      <span className="field-label">{label}</span>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">{fallback ? t.posts.strip.exampleDefault(fallback.filename) : t.posts.strip.exampleNone}</option>
        {options.map((o) => (
          <option key={o.source_id} value={o.source_id}>
            {o.filename}
          </option>
        ))}
      </select>
    </div>
  )
  const { rgb: backingRgb, opacity: backingOpacity } = splitAlpha(overlay.background)
  //: the wizard's steps (owner, 2026-10-12: "Постинг одним майстром") — what each needs, at a glance
  const steps: Step[] = [
    { label: t.posts.wizard.video, done: !!video && text.trim().length > 0, need: !video ? t.posts.wizard.needVideo : !text.trim() ? t.posts.wizard.needText : null },
    { label: t.posts.strip.title, done: true, optional: true },
    { label: t.distribute.card, done: !!cardId, optional: true },
    { label: t.posts.overlayTitle, done: !!overlayOut, optional: true },
    { label: t.posts.phones, done: phoneCount > 0, need: phoneCount > 0 ? null : t.posts.wizard.needPhones },
    { label: t.posts.launchCard, done: !!check?.ok, need: check?.ok ? null : t.posts.wizard.needCheck },
  ]

  return (
    <div className="page post-editor" style={{ padding: 0 }}>
      <div className="col">
        <StepBar steps={steps} />
        <Card className="post-step" title={<StepTitle n={1} label={t.posts.wizard.video} done={steps[0]!.done} />} sub={t.posts.editSub} actions={<Badge kind={saved === 'saved' ? 'ok' : 'warn'}>{t.posts.save[saved]}</Badge>}>
          <div className="field">
            <span className="field-label">{t.posts.video}</span>
            <select value={videoId} onChange={(e) => setVideoId(e.target.value)}>
              {readyVideos.map((v) => (
                <option key={v.video_id} value={v.video_id}>
                  {videoName(v)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <span className="field-label">{t.posts.text}</span>
            <textarea value={text} rows={4} placeholder={t.posts.textPlaceholder} onChange={(e) => setText(e.target.value)} />
          </div>
          <div className="field">
            <span className="field-label">{t.posts.banner}</span>
            <select value={bannerId} disabled={bannerBusy} onChange={(e) => setBannerId(e.target.value)}>
              <option value="">{t.posts.bannerNone}</option>
              {banners.map((b) => (
                <option key={b.source_id} value={b.source_id}>
                  {b.filename}
                </option>
              ))}
            </select>
            <div className="row">
              <input
                ref={bannerInput}
                type="file"
                accept={ACCEPT.banner}
                hidden
                onChange={(e) => {
                  void uploadBanner(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
              <button className="ghost btn-sm" disabled={bannerBusy} onClick={() => bannerInput.current?.click()}>
                {bannerBusy ? t.distribute.sending : t.posts.bannerUpload}
              </button>
            </div>
            <span className="dim tiny">{t.posts.bannerHint}</span>
            {bannerId && (
              <>
                <div className="row" style={{ marginTop: 'var(--sp-2)' }}>
                  <span className="small">{t.posts.box.anchor}</span>
                  {chips<'top' | 'bottom'>(['top', 'bottom'], box.anchor, t.posts.box, (anchor) => setBox({ ...box, anchor }))}
                </div>
                {slider(t.posts.box.width ?? 'width', box.width, BANNER_WIDTH, (v) => setBox(clampBanner({ ...box, width: v })))}
                {slider(t.posts.box.height ?? 'height', box.height, BANNER_HEIGHT, (v) => setBox(clampBanner({ ...box, height: v })))}
                {slider(t.posts.box.margin ?? 'margin', box.margin, BANNER_MARGIN, (v) => setBox(clampBanner({ ...box, margin: v })))}
              </>
            )}
          </div>
        </Card>

        <Card className="post-step" title={<StepTitle n={2} label={t.posts.strip.title} done={steps[1]!.done} optional />} sub={t.posts.strip.sub}>
          <div className="row">
            <span className="small">{t.posts.strip.side}</span>
            {chips<StripSide>(['top', 'bottom', 'random'], strip.side, t.posts.strip.sides, (side) => setStrip({ ...strip, side }))}
          </div>
          {range(t.posts.strip.height, strip.height_min, strip.height_max, STRIP_HEIGHT, (lo, hi) => setStrip({ ...strip, height_min: lo, height_max: hi }))}
          {range(t.posts.strip.opacity, strip.opacity_min, strip.opacity_max, [0, 1], (lo, hi) => setStrip({ ...strip, opacity_min: lo, opacity_max: hi }))}
          <span className="dim tiny">{t.posts.strip.rangeHint}</span>
          <div className="row" style={{ alignItems: 'flex-start', marginTop: 'var(--sp-2)' }}>
            {sourceSelect(t.posts.strip.exampleOverlay, strip.example_overlay_id, overlays, exampleOverlay, (id) => setStrip({ ...strip, example_overlay_id: id }))}
            {sourceSelect(t.posts.strip.exampleBackground, strip.example_background_id, backgrounds, exampleBackground, (id) => setStrip({ ...strip, example_background_id: id }))}
          </div>
          <span className="dim tiny">{t.posts.strip.exampleHint}</span>
          <details style={{ marginTop: 'var(--sp-2)' }}>
            <summary className="small" style={{ cursor: 'pointer' }}>{t.posts.strip.sound}</summary>
            {range(t.posts.strip.pitch, strip.pitch_min, strip.pitch_max, PITCH, (lo, hi) => setStrip({ ...strip, pitch_min: lo, pitch_max: hi }), 0.001, (v) => `±${(v * 100).toFixed(1)}%`)}
            {range(t.posts.strip.music, strip.music_volume_min, strip.music_volume_max, [0, 1], (lo, hi) => setStrip({ ...strip, music_volume_min: lo, music_volume_max: hi }), 0.005, (v) => `${(v * 100).toFixed(1)}%`)}
            <span className="dim tiny">{t.posts.strip.soundHint}</span>
          </details>
          <div className="row" style={{ marginTop: 'var(--sp-2)' }}>
            <button className="ghost btn-sm" onClick={() => setStrip({ ...DEFAULT_STRIP, example_overlay_id: strip.example_overlay_id, example_background_id: strip.example_background_id })}>
              {t.posts.strip.reset}
            </button>
          </div>
        </Card>

        <Card className="post-step" title={<StepTitle n={3} label={t.distribute.card} done={steps[2]!.done} optional />} sub={t.posts.cardSub}>
          <select value={cardId} onChange={(e) => setCardId(e.target.value)}>
            <option value="">{t.distribute.cardNone}</option>
            {cards.map((c) => (
              <option key={c.source_id} value={c.source_id}>
                {c.filename}
              </option>
            ))}
          </select>
          {cardId && (
            <>
              <div className="row" style={{ marginTop: 'var(--sp-2)' }}>
                {(['start', 'end', 'both'] as CardPlacement[]).map((p) => (
                  <label key={p} className="phone-choice is-on" style={{ cursor: 'pointer' }}>
                    <input type="radio" name="post-card-placement" checked={placement === p} onChange={() => setPlacement(p)} />
                    <span>{t.distribute.cardPlacement[p]}</span>
                  </label>
                ))}
                <input type="number" min={1} max={5} step={0.5} value={cardSeconds} onChange={(e) => setCardSeconds(Math.min(5, Math.max(1, Number(e.target.value) || 1)))} style={{ width: '4.5em' }} />
                <span className="dim small">{t.distribute.cardSeconds}</span>
              </div>
              {placement !== 'end' && <span className="tiny" style={{ color: 'var(--warn)' }}>{t.distribute.cardRetention}</span>}
            </>
          )}
        </Card>

        <Card className="post-step" title={<StepTitle n={4} label={t.posts.overlayTitle} done={steps[3]!.done} optional />} sub={t.posts.overlaySub}>
          <label className="row" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={overlayOn} onChange={(e) => setOverlayOn(e.target.checked)} />
            <span>{overlayOn ? t.posts.overlayOn : t.posts.overlayNone}</span>
          </label>
          {overlayOn && (
            <>
              <div className="field">
                <span className="field-label">{t.posts.overlayText}</span>
                <textarea value={overlay.text} rows={2} onChange={(e) => setOverlay({ ...overlay, text: e.target.value })} />
                <div className="row">
                  <button className="ghost btn-sm" disabled={!text.trim()} onClick={() => setOverlay({ ...overlay, text })}>
                    {t.posts.overlayFromCaption}
                  </button>
                </div>
              </div>
              {slider(t.posts.overlayBox.nx ?? 'x', overlay.nx, [0, 1], (v) => setOverlay({ ...overlay, nx: v }))}
              {slider(t.posts.overlayBox.ny ?? 'y', overlay.ny, [0, 1], (v) => setOverlay({ ...overlay, ny: v }))}
              {slider(t.posts.overlayBox.size ?? 'size', overlay.size, [0.02, 0.15], (v) => setOverlay({ ...overlay, size: v }), 0.005)}
              {slider(t.posts.overlayBox.width ?? 'width', overlay.width, [0.2, 1], (v) => setOverlay({ ...overlay, width: v }))}
              <div className="row" style={{ marginTop: 'var(--sp-2)' }}>
                <label className="row">
                  <span className="small">{t.posts.overlayColor}</span>
                  <input type="color" value={overlay.color.slice(0, 7)} onChange={(e) => setOverlay({ ...overlay, color: e.target.value.toUpperCase() })} />
                </label>
                <label className="row">
                  <input type="checkbox" checked={backing} onChange={(e) => setBacking(e.target.checked)} />
                  <span className="small">{t.posts.overlayBackground}</span>
                  <input
                    type="color"
                    disabled={!backing}
                    value={backingRgb}
                    onChange={(e) => setOverlay({ ...overlay, background: withAlpha(e.target.value.toUpperCase(), backingOpacity) })}
                  />
                </label>
              </div>
              {backing && slider(t.posts.overlayBackgroundOpacity, backingOpacity, [0, 1], (v) => setOverlay({ ...overlay, background: withAlpha(backingRgb, v) }))}
            </>
          )}
        </Card>

        <Card className="post-step" title={<StepTitle n={5} label={t.posts.phones} done={steps[4]!.done} />} sub={t.posts.phonesSub} actions={sources.data ? <Badge kind={phoneCount > capacity ? 'bad' : capacity > 0 ? 'ok' : 'warn'}>{t.distribute.capacity(phoneCount, capacity)}</Badge> : null}>
          <TargetPicker hubs={hubs} devices={devices} picked={picked} onChange={setPicked} direction={video?.direction_id} />
          <span className="dim tiny">{t.distribute.delaysHint}</span>
        </Card>

        <Card className="post-step" title={<StepTitle n={6} label={t.posts.launchCard} done={steps[5]!.done} />}>
          {check && check.problems.length > 0 && (
            <ul className="small" style={{ color: 'var(--bad)', paddingLeft: '1.4em' }}>
              {check.problems.map((p) => (
                <li key={p.code}>
                  {t.posts.problems[p.code] ?? p.hint}
                  {p.device_ids && p.device_ids.length > 0 && (
                    <>: <b>{p.device_ids.map((id) => { const d = devices.find((x) => x.device_id === id); return d ? phoneName(d) : id }).join(', ')}</b></>
                  )}
                </li>
              ))}
            </ul>
          )}
          {check?.ok && (
            <p className="small" style={{ color: 'var(--ok)' }}>
              {t.posts.readyToLaunch(check.phones.length, check.scenario ?? '')}
            </p>
          )}
          <div className="actions">
            <button className="ghost" disabled={saved !== 'saved'} onClick={() => void runCheck()}>
              {t.posts.check}
            </button>
            {/* a look at the variant each phone gets before anything is pushed (tester, 2026-10-15) */}
            <button className="ghost" disabled={!check?.ok || saved !== 'saved'}
                    onClick={() => { setHoldLaunch(true); setConfirm(true) }}>
              {t.posts.launchHeld}
            </button>
            <button className="primary" disabled={!check?.ok || saved !== 'saved'} onClick={() => { setHoldLaunch(false); setConfirm(true) }}>
              {t.posts.launch}
            </button>
          </div>
        </Card>
      </div>

      <div className="col">
        <Preview
          postId={post.post_id}
          version={version}
          saved={saved === 'saved'}
          video={video}
          banner={banner}
          box={bannerId ? box : null}
          overlay={overlayOut}
          caption={text}
          strip={strip}
          exampleOverlay={exampleOverlay}
          exampleBackground={exampleBackground}
        />
      </div>
      {confirm && (
        <ConfirmDanger title={t.posts.launchTitle(check?.phones.length ?? 0)} confirmLabel={t.posts.launch} holdSeconds={2} onCancel={() => setConfirm(false)} onConfirm={() => void launch()}>
          <p className="dim small">{holdLaunch ? t.posts.launchHeldBody : t.posts.launchBody}</p>
        </ConfirmDanger>
      )}
    </div>
  )
}

const FRAME_H = 640

/**
 * A signed artifact link is re-issued on every poll, and a `<video>` whose `src` changes starts over —
 * the in-motion preview never got past its first frame. Keep the link we have until it is about to
 * expire; the id says whether it is still the same file.
 */
function useStableUrl(id: string | null | undefined, url: string | null | undefined): string | null {
  const kept = useRef<{ id: string; url: string } | null>(null)
  if (!id || !url) {
    kept.current = null
    return null
  }
  const current = kept.current
  if (current && current.id === id) {
    const exp = Number(new URL(current.url, 'http://x').searchParams.get('exp') ?? 0)
    if (exp - Date.now() / 1000 > 120) return current.url
  }
  kept.current = { id, url }
  return url
}

/** The overlay text drawn the browser's way for the in-motion view: same fractions, approximate glyphs. */
function overlayStyle(o: OverlayChoice, frameHeight: number): CSSProperties {
  return {
    position: 'absolute',
    left: `${o.nx * 100}%`,
    top: `${o.ny * 100}%`,
    transform: 'translate(-50%, -50%)',
    // max-content, or an absolutely positioned box shrinks to what is left of its containing block
    // to the right of `left` and wraps every word
    width: 'max-content',
    maxWidth: `${o.width * 100}%`,
    fontSize: `${o.size * frameHeight}px`,
    lineHeight: 1.22,
    color: o.color,
    background: o.background ?? 'transparent',
    padding: o.background ? `${o.size * frameHeight * 0.45}px` : 0,
    borderRadius: `${o.size * frameHeight * 0.35}px`,
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontWeight: 700,
    pointerEvents: 'none',
  }
}

/** The exact frame from the service, or the strip and the banner in motion over the video in the browser. */
function Preview({
  postId,
  version,
  saved,
  video,
  banner,
  box,
  overlay,
  caption,
  strip,
  exampleOverlay,
  exampleBackground,
}: {
  postId: string
  version: number
  saved: boolean
  video: Video | null
  banner: Source | null
  box: BannerGeometry | null
  overlay: OverlayChoice | null
  caption: string
  strip: StripSettings
  exampleOverlay: Source | null
  exampleBackground: Source | null
}) {
  const { api } = useApp()
  const t = useT()
  const [mode, setMode] = useState<'frame' | 'motion'>('frame')
  const [at, setAt] = useState(1)
  const [frame, setFrame] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const duration = Math.max(0.5, (video?.duration_s ?? 10) - 0.1)
  const atClamped = Math.min(at, duration)

  // One request in flight at a time: a new change aborts the old one, and the last good frame stays
  // on screen until the new one arrives — the tester saw frames arrive out of order and vanish.
  useEffect(() => {
    if (mode !== 'frame' || !video || !saved) return
    const controller = new AbortController()
    const handle = setTimeout(() => {
      setLoading(true)
      api
        .frame(postId, atClamped, controller.signal)
        .then((blob) => {
          setFrame((old) => {
            if (old) URL.revokeObjectURL(old)
            return URL.createObjectURL(blob)
          })
          setError(null)
        })
        .catch((e) => {
          if (controller.signal.aborted) return
          setError(e instanceof Error ? e.message : String(e))
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false)
        })
    }, 300)
    return () => {
      clearTimeout(handle)
      controller.abort()
    }
  }, [api, mode, video, saved, postId, version, atClamped])

  const bannerIsGif = banner?.filename.toLowerCase().endsWith('.gif') ?? false
  const bannerSrc = useStableUrl(banner?.source_id, banner?.preview_url ?? banner?.url)
  const videoSrc = useStableUrl(video?.video_id, video?.url)
  const overlaySrc = useStableUrl(exampleOverlay?.source_id, exampleOverlay?.url)
  const backgroundSrc = useStableUrl(exampleBackground?.source_id, exampleBackground?.url)
  // The strip as the service cuts it: the frame covers the canvas, the strip replaces its top or bottom
  // `height_max` — drawn over that part of the video, which is the same picture as cropping it away.
  const stripStyle: CSSProperties = {
    position: 'absolute',
    left: 0,
    width: '100%',
    height: `${strip.height_max * 100}%`,
    [previewSide(strip.side)]: 0,
    overflow: 'hidden',
    background: backgroundSrc ? `url(${api.artifactUrl(backgroundSrc)}) center / cover` : '#222',
    pointerEvents: 'none',
  }

  return (
    <Card
      title={t.posts.preview}
      sub={t.posts.previewHint}
      actions={
        <span className="chips">
          <button className={`chip${mode === 'frame' ? ' on' : ''}`} onClick={() => setMode('frame')}>
            {t.posts.previewFrame}
          </button>
          <button className={`chip${mode === 'motion' ? ' on' : ''}`} onClick={() => setMode('motion')}>
            {t.posts.previewMotion}
          </button>
        </span>
      }
    >
      {!video && <Empty>{t.posts.previewNoVideo}</Empty>}
      {video && (
        <div style={{ position: 'relative', aspectRatio: '9 / 16', height: FRAME_H, maxWidth: '100%', margin: '0 auto', background: '#000', overflow: 'hidden', borderRadius: 'var(--r-sm)' }}>
          {mode === 'frame' && frame && <img src={frame} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', opacity: loading ? 0.6 : 1 }} />}
          {mode === 'frame' && loading && (
            <span className="tiny" style={{ position: 'absolute', top: 8, left: 8, padding: '2px 8px', borderRadius: 999, background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
              {t.posts.previewLoading}
            </span>
          )}
          {mode === 'motion' && videoSrc && (
            <>
              <video src={api.artifactUrl(videoSrc)} autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              {(exampleOverlay || exampleBackground) && (
                <div aria-hidden style={stripStyle}>
                  {overlaySrc && <video src={api.artifactUrl(overlaySrc)} autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: strip.opacity_max }} />}
                </div>
              )}
              {bannerSrc && box && bannerIsGif && (
                <img src={api.artifactUrl(bannerSrc)} alt="" style={{ position: 'absolute', objectFit: 'contain', pointerEvents: 'none', ...bannerBoxStyle(box) }} />
              )}
              {bannerSrc && box && !bannerIsGif && (
                <video src={api.artifactUrl(bannerSrc)} autoPlay loop muted playsInline style={{ position: 'absolute', objectFit: 'contain', pointerEvents: 'none', ...bannerBoxStyle(box) }} />
              )}
              {overlay && <div style={overlayStyle(overlay, FRAME_H)}>{overlay.text}</div>}
            </>
          )}
          {box && <div aria-hidden style={{ position: 'absolute', border: '1px dashed var(--acc)', pointerEvents: 'none', ...bannerBoxStyle(box) }} />}
          {caption.trim() && (
            <div
              title={t.posts.previewCaption}
              style={{ position: 'absolute', left: 12, right: 64, bottom: 14, color: '#fff', fontSize: 12, lineHeight: 1.3, textShadow: '0 1px 2px rgba(0,0,0,0.8)', pointerEvents: 'none', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {caption}
            </div>
          )}
        </div>
      )}
      {video && caption.trim() && <span className="dim tiny">{t.posts.previewCaption}</span>}
      {mode === 'frame' && video && (
        <div className="slider-row">
          <span className="dim small">{t.posts.previewAt}</span>
          <input type="range" min={0} max={duration} step={0.5} value={atClamped} onChange={(e) => setAt(Number(e.target.value))} />
          <span className="mono small">{atClamped}s</span>
        </div>
      )}
      <Status text={error} bad />
    </Card>
  )
}

/** After launch: one stage per phone, the reason when it failed, and "Post again" on a person's click. */
function LaunchedPost({ post, onChanged }: { post: Post; onChanged: () => void }) {
  const { api, authEpoch } = useApp()
  const t = useT()
  const navigate = useNavigate()
  const devices = usePoll(() => api.devices(), 15000, [api, authEpoch])
  const byId = new Map((devices.data ?? []).map((d) => [d.device_id, d]))
  const [retrying, setRetrying] = useState<string | null>(null)

  const retry = async (deviceId: string) => {
    if (!post.distribution_id) return
    setRetrying(deviceId)
    try {
      await api.retryPublish(post.distribution_id, deviceId)
      toast(t.distributions.publishRetried(deviceId))
      onChanged()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    } finally {
      setRetrying(null)
    }
  }

  const retryPush = async (deviceId: string) => {
    if (!post.distribution_id) return
    setRetrying(deviceId)
    try {
      const r = await api.retryPush(post.distribution_id, deviceId)
      toast(t.distributions.pushRetried(deviceId, r.try, r.of))
      onChanged()
    } catch (e) {
      const detail = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
      toast(detail?.hint ?? (e instanceof Error ? e.message : String(e)), 'bad')
    } finally {
      setRetrying(null)
    }
  }

  const phones = post.phones ?? []
  return (
    <Card
      title={t.posts.launchedTitle}
      sub={post.text}
      actions={
        <>
          {post.distribution && <Badge kind={post.distribution.status === 'done' ? 'ok' : post.distribution.status === 'running' ? 'warn' : 'bad'}>{t.distributions.status[post.distribution.status]}</Badge>}
          {post.distribution_id && (
            <button className="ghost btn-sm" onClick={() => navigate(`/distributions/${post.distribution_id}`)}>
              {t.posts.openDistribution}
            </button>
          )}
        </>
      }
    >
      {phones.length === 0 && <Empty>{t.distribute.loading}</Empty>}
      {phones.length > 0 && (
        <div className="scroll-x">
          <table className="grid">
            <thead>
              <tr>
                <th>{t.posts.phone}</th>
                <th>{t.posts.stage}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {phones.map((p) => {
                const d = byId.get(p.device_id)
                return (
                  <tr key={p.device_id}>
                    <td>
                      {d ? phoneName(d) : p.device_id} <span className="dim tiny">· {p.hub_id}</span>
                    </td>
                    <td>
                      <Badge kind={stageKind(p.stage)}
                             title={p.stage === 'by_hand' ? t.distributions.byHandTitle
                               : p.stage === 'unconfirmed' ? t.distributions.unconfirmedTitle : undefined}>{t.posts.stages[p.stage]}</Badge>
                      {p.url && (
                        <> <a className="tiny" href={p.url} target="_blank" rel="noreferrer">{t.distributions.openPost}</a></>
                      )}
                      {p.stage === 'error' && (
                        <div className="tiny" style={{ color: 'var(--bad)' }}>
                          {p.failed_at ? `${t.posts.failedAt[p.failed_at] ?? p.failed_at}: ` : ''}
                          {p.error}
                          {p.screenshot && (
                            <>
                              {' · '}
                              <a href={api.artifactUrl(p.screenshot)} target="_blank" rel="noreferrer">
                                {t.distributions.publishScreenshot}
                              </a>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {p.stage === 'error' && p.failed_at === 'pushing' && (
                        <button className="btn-sm" disabled={retrying === p.device_id} onClick={() => void retryPush(p.device_id)}>
                          {t.distributions.retryPush}
                        </button>
                      )}
                      {p.stage === 'error' && p.failed_at === 'publishing' && (
                        <button className="btn-sm" disabled={retrying === p.device_id} onClick={() => void retry(p.device_id)}>
                          {t.distributions.publishRetry}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
